import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

const buttonStyles = cva(
  [
    "inline-flex items-center justify-center gap-2 rounded-xl font-medium",
    "transition-[background,box-shadow,transform] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    "disabled:opacity-50 disabled:pointer-events-none",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-gradient-to-b from-amber to-coral text-[#10141d]",
          "shadow-glow-amber hover:brightness-105",
          "font-serif font-bold tracking-tight",
        ].join(" "),
        secondary: [
          "bg-surface border border-stroke-strong text-ink",
          "hover:bg-surface-raised hover:border-amber/40",
        ].join(" "),
        ghost: [
          "bg-transparent text-ink-muted",
          "hover:bg-surface hover:text-ink",
        ].join(" "),
        danger: [
          "bg-loss/90 text-white",
          "hover:bg-loss",
        ].join(" "),
        outline: [
          "bg-transparent border border-stroke-strong text-ink-muted",
          "hover:bg-surface hover:text-ink hover:border-amber/40",
        ].join(" "),
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "md",
    },
  },
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonStyles>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonStyles({ variant, size, fullWidth }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
